import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  Menu,
  InputLabel,
  FormControl,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Divider,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Drawer,
  IconButton,
  Tooltip,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  CloudDownload,
  Upload,
  GetApp,
  Description,
  HealthAndSafety,
  Visibility,
} from "@mui/icons-material";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import LoadingOverlay from "../components/LoadingOverlay";
import { useSelector, useDispatch } from "react-redux";
import {
  setClassificationResult,
  setApplicationSetupData,
  setLoading as setAppSetupLoading,
  setError as setAppSetupError,
} from "../store/slices/applicationSetupSlice";

const INTEGRATION_TOOLS = [
  "Salesforce",
  "SAP",
  "Google Drive",
  "Slack",
  "Microsoft Teams",
  "Jira",
];
const VECTOR_DATABASES = ["FAISS", "Pinecone", "Weaviate", "Milvus", "Qdrant"];
const SERIOUSNESS_OPTIONS = ["Low", "Medium", "High", "Critical"];
const DATA_SOURCE_OPTIONS = ["Production", "Staging", "Development", "Test"];
const RISK_ASSOCIATION_OPTIONS = [
  "Hallucination",
  "Bias",
  "Privacy Leak",
  "Toxicity",
  "Drift",
  "Security",
];
const EVALUATION_SCOPE_OPTIONS = [
  "Faithfulness",
  "Bias",
  "Privacy",
  "Robustness",
  "Toxicity",
  "Fairness",
];

const steps = [
  "Basic Application Information",
  "Technical Details (UI/API)",
  "Use Case",
];

// Example data for each step
const EXAMPLE_DATA = {
  step1: {
    applicationName: "Life Guard Health Insurance",
    domain: "Insurance",
    description:
      "AI-powered assistant for insurance policy management and customer queries",
    organization: "Life Guard",
    deploymentEnvironment: "QA",
    dataSensitivity: ["PII", "PHI"],
    autonomyLevel: ["Decisioning", "Action-Taking"],
    modifiers: ["RAG", "ENT"],
    riskContextNotes:
      "Extremely high-risk application handling sensitive patient data (PHI) and financial transactions. Requires HIPAA compliance, strong data encryption, and robust safety guardrails",
  },
  step2: {
    interfaceType: "Hybrid",
    llmProvider: "AWS-Bedrock",
    llmModel: "Llama 3",
    frontendStack: "React, TypeScript",
    backendStack: "Node.js, Express",
    apiEndpoints: [
      "https://lifeguard4u.in/chat",
      "https://lifeguard4u.in/validate-policy",
      "https://lifeguard4u.in/",
    ],
    ragEmbedding: "AWS",
    ragVector: "FAISS",
    ragRetrieval: "Dense",
    authMethod: "API Key",
    authToken: "*******************************************************",
    aiUrl: "https://insurance-assistant.example.com",
    logsApi: "https://lifeguard4u.in/logs",
  },
  step3: [
    {
      title: "Premium Retrieval & Calculation",

      severity: "High",

      description:
        "The user queries the chatbot to retrieve, compare, or estimate insurance premiums based on policy type, applicant profile, sum insured, add-ons, and underwriting rules.",

      expected:
        "The system should accurately calculate or retrieve the premium using valid underwriting formulas, transparently show influencing factors, and avoid hallucinated or unsupported financial outputs.",
    },

    {
      title: "Coverage Details Retrieval",

      severity: "High",

      description:
        "The user requests information about what specific benefits or coverage items are included under their insurance policy.",

      expected:
        "The chatbot must retrieve the correct coverage clause from the knowledge base and deliver accurate, evidence-backed information without fabricating benefits.",
    },

    {
      title: "Waiting Period Clarification",

      severity: "High",

      description:
        "The user asks about the applicable waiting period for benefits such as maternity, pre-existing diseases, or critical illness coverage.",

      expected:
        "The system retrieves the exact waiting period clause (e.g., 24 months or 48 months) from the policy document and returns it with proper grounding.",
    },

    {
      title: "Claim Status Reason Explanation",

      severity: "Critical",

      description:
        "The user queries the chatbot to understand why their claim was approved, delayed, or rejected based on internal claim assessment guidelines.",

      expected:
        "The bot must provide an accurate explanation grounded in retrieved claim rules or rejection reasons without inventing information or giving unsupported assurances.",
    },

    {
      title: "Premium Factor Breakdown",

      severity: "Medium",

      description:
        "The user requests an explanation of how their final premium was computed, including age, add-ons, sum insured, underwriting loadings, and policy rules.",

      expected:
        "The chatbot retrieves the underwriting guideline and provides a transparent, clause-backed premium factor breakdown without hallucinating formulas.",
    },

    {
      title: "Exclusion Clause Identification",

      severity: "High",

      description:
        "The user asks whether a particular treatment or condition (e.g., cataract surgery, bariatric surgery) is covered or excluded under their policy.",

      expected:
        "The system retrieves the exact exclusion clause and provides a grounded, accurate response that reflects the true exclusions or partial coverage listed.",
    },

    {
      title: "Policy Renewal Requirement Check",

      severity: "Medium",

      description:
        "The user asks whether any medical tests or documentation are required when renewing their policy.",

      expected:
        "The bot retrieves the renewal guidelines from the KB and returns the precise rules without making up new requirements or skipping mandatory steps.",
    },

    {
      title: "Co-pay Information Retrieval",

      severity: "High",

      description:
        "The user queries the applicable co-pay percentage or amount for their policy or treatment type.",

      expected:
        "The system retrieves the exact co-pay clause and returns correct information backed by evidence from policy documents.",
    },

    {
      title: "Network Hospital Eligibility Check",

      severity: "Medium",

      description:
        "The user asks whether a specific hospital is part of the insurer's network hospital list for cashless treatment.",

      expected:
        "The chatbot retrieves the network hospital list and confirms eligibility accurately without guessing or missing entries.",
    },

    {
      title: "Add-on Benefit Details",

      severity: "Low",

      description:
        "The user requests details about optional add-ons such as critical illness cover, hospital cash, or accidental benefit riders.",

      expected:
        "The chatbot retrieves the add-on definition and returns accurate coverage details and exclusions without hallucinating features.",
    },
  ],
};

// Example data 2 - Vision AI Application
const EXAMPLE_DATA_2 = {
  step1: {
    applicationName: "RedPlanet AdSafe Vision",
    domain: "Advertisment",
    description:
      "Gen‑AI studio that generates, scores, and iteratively optimizes ad images and short‑form videos for Mars brands using historical performance and attention‑heatmap data.",
    organization: "Mars",
    deploymentEnvironment: "Production",
    dataSensitivity: ["Confidential"],
    autonomyLevel: ["Action-Taking"],
    modifiers: ["AG", "MM"],
    riskContextNotes:
      "High-risk vision AI application processing sensitive surveillance footage. Requires robust safety detection for violence, weapons, and inappropriate content. Must comply with privacy regulations for biometric data.",
  },
  step2: {
    interfaceType: "UI",
    llmProvider: "OpenAI",
    llmModel: "GPT-4o Vision",
    frontendStack: "React, TypeScript",
    backendStack: "Python, FastAPI",
  },
  step3: [
    {
      title: "Time Brand Asset Customization",
      severity: "High",
      description:
        "The application ingests brand guidelines, color palettes, logos, and text specs via a live interface, instantly generating tailored images like banners or social graphics during client meetings or campaigns.",
      expected:
        "On-demand, spec-compliant visuals that evolve with feedback, enabling instant iterations for presentations or launches without design delays.",
    },
  ],
};


// / Example data 3 - Agentic AI Application(A9/A12)(39)
// const EXAMPLE_DATA_3 = {
//   step1: {
//     applicationName: "Wilson-Carter Systems",
//     domain: "Banking",
//     description:
//       "Automation tool that uses AI to perform tasks autonomously across different systems and tools.",
//     organization: "Thompson solutions ",
//     deploymentEnvironment: "On-Prem",
//     dataSensitivity: ["Financial"],
//     autonomyLevel: ["Autonomous"],
//     modifiers: ["Multimodal(MM)"],
//     riskContextNotes:
//       "This application handles critical processes,where failure can result in severe operational disruptions or financial losses",
//   },
//   step2: {
//     interfaceType: "API",
//     llmProvider: "Google",
//     llmModel: "gemini-pro",
//     frontendStack: "Angular",
//     backendStack: "Flask",
//     apiEndpoints: [
//       "http://www.wilson-carter-systems.biz/api/v2/query",
//     ],
//     ragEmbedding: "Google",
//     ragVector: "Qdrant",
//     ragRetrieval: "Hybrid",
//     authMethod: "Bearer Token",
//     authToken: "35278de6-bfc3-4401-a0d2-68c4505dade5",
//     aiUrl: "http://www.wilson-carter-systems.biz/",
//     logsApi: "https://www.thompson-solutions.org/",
//   },
//   step3: [
//     {
//       title: "Secure Customer Profile Retrieval",

//       severity: "High",

//       description:
//         "A Customer Profile Agent retrieves customer KYC, account details, and relationship data when triggered by other agents (e.g., Loan Agent, Support Agent). This agent must ensure correct identity resolution and data privacy while serving downstream agents.",

//       expected:
//         "Validate customer identity before data retrieval,Fetch only minimum required customer attributes,Mask or redact sensitive fields when not required,Enforce consent and access policies,Log every inter-agent data exchange for audit",
//     },

//   ],
// };

const EXAMPLE_DATA_3 = {
  step1: {
    applicationName: "Enterprise Healthcare Provider / Hospital Network",
    domain: "Healthcare",
    description:
      "AI-powered clinical documentation assistant that retrieves patient records and clinician notes to generate visit summaries, discharge notes, and clinical documentation",
    organization: "Hospital Network ",
    deploymentEnvironment: "Dev",
    dataSensitivity: ["PHI","PII"],
    autonomyLevel: ["Advisory"],
    modifiers: ["RAG"],
    riskContextNotes:
      "High regulatory exposure due to PHI usage; risks include data leakage, hallucinated clinical content, and incorrect patient-context retrieval",
  },
  step2: {
    interfaceType: "API",
    llmProvider: "OpenAI",
    llmModel: "gpt-4o",
    frontendStack: "python",
    backendStack: "python",
    apiEndpoints: [
      "http://54.146.218.11:5001//predict",
    ],
    ragEmbedding: "text-embeddings-002",
    ragVector: "FAISS",
    ragRetrieval: "Hybrid",
    authMethod: "API Key",
    authToken: "********************************",
    aiUrl: "http://www.wilson-carter-systems.biz/",
    logsApi: "https://www.thompson-solutions.org/",
  },
  step3: [
    {
      title: "Inpatient Admission & Clinical Documentation Accuracy Validation",

      severity: "High",

      description:
        "Validation of accuracy, completeness, and traceability of inpatient clinical documentation across emergency triage, admission notes, physician documentation, nursing notes, diagnostics, and discharge records.",

      expected:
        "Consistent patient identifiers, accurate admission diagnosis, allergies documented before medication orders, clear chronological clinical notes, and audit-ready documentation.",
    },


    {

      title: "Diagnostic Order‑to‑Result Integrity (Laboratory & Imaging)",

      severity: "High",

      description: "Validation that laboratory and imaging diagnostic orders and results are correctly linked to the appropriate patient across EHR, LIS, and RIS systems.",

      expected:
        "Correct patient association, SLA-compliant turnaround times, documented critical result escalation, and no cross-patient data exposure.",

    },

    {

      title: "Inpatient Medication Management & Drug Station Safety",

      severity: "High",

      description: "Validation of safe medication management from prescribing through pharmacy verification, drug station dispensing, and bedside administration.",

      expected:
        "Enforced allergy and interaction checks, barcode medication administration, controlled substance reconciliation, and complete medication audit trails.",

    },
    {

      title: "Outpatient Visit Documentation & Follow‑Up Continuity",

      severity: "High",

      description: "Validation that outpatient encounters are fully documented with clear follow-up instructions, referrals, and diagnostic actions.",

      expected:
        "Complete visit documentation, recorded follow-up instructions, traceable referrals, and accurate patient portal updates.",

    },
    {

      title: "Billing Accuracy & Charge Capture Validation",

      severity: "High",

      description: "Validation that billed charges accurately reflect clinical services delivered using ICD-10 and CPT coding.",

      expected:
        "Aligned diagnosis and procedure codes, no duplicate charges, traceable claim rejections, and audit-compliant billing records.",

    },

        {

      title: "Discharge Summary Completeness & Care Transition Safety",

      severity: "High",

      description: "Validation that discharge summaries are complete, timely, and support safe patient care transitions.",

      expected:
        "SLA-compliant discharge summaries, documented medication changes, scheduled follow-ups, and continuity-of-care support.",

    },
            {

      title: "Patient Portal Accuracy & Privacy Assurance",

      severity: "High",

      description: "Validation of accuracy and privacy controls for patient-facing information in the patient portal.",

      expected:
        "Correct patient data visibility, enforced role-based access, complete audit logs, and no unauthorized data exposure.",

    },


  ],
};


const baseUrl = process.env.REACT_APP_API_URL || "https://validaite.io/api";
const SIDEBAR_WIDTH = 0;
console.log("Base_URL: ", baseUrl);

const ApplicationSetupPage = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [classificationResult, setClassificationResultState] = useState(null);

  const reduxProjectId = useSelector((state) => state.project.currentProjectId);
  const currentProjectId = projectId || reduxProjectId;

  // Step 1: Basic Application Information
  // const [basicInfo, setBasicInfo] = useState({
  //   applicationName: '',
  //   domain: '',
  //   description: '',
  // });
  const [basicInfo, setBasicInfoState] = useState({
    applicationName: "",
    domain: "",
    description: "",
    organization: "",
    deploymentEnvironment: "",
    businessOwner: "",
    technicalOwner: "",
    dataSensitivity: [],
    autonomyLevel: [],
    modifiers: [],
    riskContextNotes: "",
  });
  // === Modal Styles ===
  const modalBackdropStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  };

  const modalCardStyle = {
    width: "45%",
    background: "white",
    padding: "24px",
    borderRadius: "12px",
    boxShadow: "0px 8px 22px rgba(0,0,0,0.15)",
    animation: "fadeIn 0.25s ease-out",
  };

  const dropAreaStyle = {
    border: "2px dashed #0094FB",
    borderRadius: "10px",
    padding: "35px",
    textAlign: "center",
    cursor: "pointer",
    color: "#444",
    fontSize: "15px",
    backgroundColor: "#F9FBFF",
  };

  const [uploadedContent, setUploadedContent] = useState(null);
  const [openUseCaseDrawer, setOpenUseCaseDrawer] = useState(false);

  // Modal visibility states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDefineModal, setShowDefineModal] = useState(false);

  // Drag & drop uploaded file storage
  const [uploadedFile, setUploadedFile] = useState(null);

  // List of saved custom use cases
  const [useCasesList, setUseCasesListState] = useState([]);

  // Temp form state for Define Use Case
  const [defineCaseForm, setDefineCaseForm] = useState({
    title: "",
    severity: "",
    description: "",
    expected: "",
    dataSource: "",
    smeOwner: "",
    riskAssociation: [],
    evaluationScope: [],
  });

  // handle upload
  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.type.includes("json") || file.type.includes("xml"))) {
      setUploadedFile(file);
    }
  };

  const handleBrowseFile = (e) => {
    const file = e.target.files[0];
    if (file && (file.type.includes("json") || file.type.includes("xml"))) {
      setUploadedFile(file);
    }
  };
  const handleUploadFile = () => {
    if (!uploadedFile) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      let content = e.target.result;

      // Display content stored into useCasesList
      setUseCasesListState((prev) => [
        ...prev,
        { fileName: uploadedFile.name, content },
      ]);

      alert("File uploaded successfully!");
    };

    reader.readAsText(uploadedFile);

    // Close drawer after upload
    setShowUploadModal(false);
  };

  const handleDefineInput = (e) => {
    setDefineCaseForm({ ...defineCaseForm, [e.target.name]: e.target.value });
  };

  const toggleRisk = (value) => {
    setDefineCaseForm((prev) => ({
      ...prev,
      riskAssociation: prev.riskAssociation.includes(value)
        ? prev.riskAssociation.filter((v) => v !== value)
        : [...prev.riskAssociation, value],
    }));
  };

  const toggleScope = (value) => {
    setDefineCaseForm((prev) => ({
      ...prev,
      evaluationScope: prev.evaluationScope.includes(value)
        ? prev.evaluationScope.filter((v) => v !== value)
        : [...prev.evaluationScope, value],
    }));
  };
  // ================= DIRECT FILE UPLOAD HANDLER =================
  const handleChooseFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const ext = file.name.toLowerCase();
    const allowed = [".json", ".pdf", ".doc", ".docx"];

    if (!allowed.some((type) => ext.endsWith(type))) {
      alert("Please upload JSON, PDF, DOC, or DOCX");
      return;
    }

    // Handle JSON files
    if (ext.endsWith(".json")) {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const json = JSON.parse(reader.result);
          setUploadedContent(json);
          setUseCasesListState((prev) => [...prev, json]);
        } catch (err) {
          alert("Invalid JSON file");
        }
      };
      reader.readAsText(file);
      return;
    }

    // Handle PDF, DOC, DOCX (cannot fully extract text in browser easily)
    setUploadedContent({
      fileName: file.name,
      message:
        "File uploaded — extracted content unavailable in browser preview.",
    });

    setUseCasesListState((prev) => [...prev, { fileName: file.name }]);
  };

  const handleSaveDefinedUseCase = () => {
    // === DIRECT FILE UPLOAD (NO MODAL) ===
    // const handleChooseFileUpload = (event) => {
    //   const file = event.target.files[0];
    //   if (!file) return;

    //   if (!(file.name.endsWith(".json") || file.name.endsWith(".xml"))) {
    //     alert("Please upload a valid JSON or XML file");
    //     return;
    //   }

    //   const reader = new FileReader();
    //   reader.onload = (e) => {
    //     try {
    //       let parsedData;

    //       if (file.name.endsWith(".json")) {
    //         parsedData = JSON.parse(e.target.result);
    //       } else {
    //         alert("XML parsing not supported yet");
    //         return;
    //       }

    //       if (Array.isArray(parsedData)) {
    //         setUseCasesList(prev => [...prev, ...parsedData]);
    //       } else {
    //         setUseCasesList(prev => [...prev, parsedData]);
    //       }
    //     } catch (err) {
    //       console.error("Parsing Error:", err);
    //       alert("Invalid JSON. Please check the format.");
    //     }
    //   };
    //   reader.readAsText(file);
    // };

    setUseCasesListState([...useCasesList, defineCaseForm]);
    setShowDefineModal(false);
    setDefineCaseForm({
      title: "",
      severity: "",
      description: "",
      expected: "",
      dataSource: "",
      smeOwner: "",
      riskAssociation: [],
      evaluationScope: [],
    });
  };

  const handleModifierCheck = (modifier) => {
    setBasicInfoState((prev) => ({
      ...prev,
      modifiers: prev.modifiers.includes(modifier)
        ? prev.modifiers.filter((m) => m !== modifier)
        : [...prev.modifiers, modifier],
    }));
  };
  const handleAutonomyLevelToggle = (item) => {
    setBasicInfoState((prev) => ({
      ...prev,
      autonomyLevel: prev.autonomyLevel.includes(item)
        ? prev.autonomyLevel.filter((i) => i !== item)
        : [...prev.autonomyLevel, item],
    }));
  };

  const handleDataSensitivityToggle = (item) => {
    setBasicInfoState((prev) => ({
      ...prev,
      dataSensitivity: prev.dataSensitivity.includes(item)
        ? prev.dataSensitivity.filter((i) => i !== item)
        : [...prev.dataSensitivity, item],
    }));
  };

  // Step 2: Technical Details
  const [technicalDetails, setTechnicalDetailsState] = useState({
    interfaceType: "",
    llmProvider: "",
    llmModel: "",
    frontendStack: "",
    // onpremises: '',
    // cloud: '',
    backendStack: "",
    apiEndpoints: [],
    ragEmbedding: "",
    ragVector: "",
    ragRetrieval: "",
    authMethod: "",
    authToken: "",
    aiUrl: "",
    // uiInputSelector: '',
    // uiOutputSelector: '',
    integrationTools: [],
    logsApi: "",
    newEndpoint: "",
    agentEnabled: false,
    agentFramework: "",
    agentType: "",
    agentPlanner: "",
    agentMemory: "",
    agentTools: [],
    customAgentTool: "",
  });

  // Step 3: Functional Use Case Catalog
  const [useCaseCatalog, setUseCaseCatalogState] = useState({
    useCaseTitle: "",
    seriousness: "",
    useCaseDescription: "",
    expectedBehavior: "",
    dataSource: "",
    smeOwner: "",
    riskAssociation: [],
    evaluationScope: [],
  });

  // Load saved data on mount
  useEffect(() => {
    if (!currentProjectId) return;

    console.log("📥 Loading application setup for project:", currentProjectId);
    dispatch(setAppSetupLoading(true));
    axios
      .get(`${baseUrl}/application-setup?project_id=${currentProjectId}`)
      .then((res) => {
        console.log("📦 Application setup response:", res.data);
        if (res.data && res.data.data) {
          const data = res.data.data;
          console.log("📋 Loaded data:", {
            basicInfo: data.basicInfo ? "present" : "missing",
            technicalDetails: data.technicalDetails ? "present" : "missing",
            useCasesList: data.useCasesList?.length || 0,
            classificationResult: data.classificationResult ? "present" : "missing",
            currentStep: data.currentStep,
          });

          // Update local state
          if (data.basicInfo) setBasicInfoState(data.basicInfo);
          if (data.technicalDetails)
            setTechnicalDetailsState(data.technicalDetails);
          if (data.useCaseCatalog) setUseCaseCatalogState(data.useCaseCatalog);
          if (data.useCasesList) setUseCasesListState(data.useCasesList);
          if (data.classificationResult) {
            console.log("🎯 Setting classification result:", data.classificationResult);
            setClassificationResultState(data.classificationResult);
          }
          if (data.currentStep) setActiveStep(data.currentStep);

          // Update Redux store
          dispatch(setApplicationSetupData(data));
        }
      })
      .catch((err) => {
        console.error("Error loading application setup:", err);
        dispatch(setAppSetupError("Failed to load data"));
      })
      .finally(() => {
        dispatch(setAppSetupLoading(false));
      });
  }, [currentProjectId, dispatch]);
  // useEffect(() => {
  //   const fetchApplicationSetup = async () => {
  //     if (!projectId) {
  //       console.log("No projectId, skipping fetch");
  //       return;
  //     }

  //     try {
  //       dispatch(setAppSetupLoading(true));
  //       const response = await axios.get(
  //         `${baseUrl}/application-setup?project_id=${projectId}`
  //       );

  //       if (response.data.success && response.data.data) {
  //         console.log("Fetched application setup data:", response.data.data);
  //         dispatch(setApplicationSetupData(response.data.data));
  //       }
  //     } catch (err) {
  //       console.error("Error fetching application setup:", err);
  //       dispatch(setAppSetupError("Failed to load data"));
  //     } finally {
  //       dispatch(setAppSetupLoading(false));
  //     }
  //   };

  //   fetchApplicationSetup();
  // }, [projectId, dispatch]);

  // Validation functions for each step
  const validateStep1 = () => {
    if (!basicInfo.applicationName.trim()) {
      setError("Application Name is required");
      return false;
    }
    if (!basicInfo.domain.trim()) {
      setError("Domain is required");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!technicalDetails.interfaceType) {
      setError("Interface Type is required");
      return false;
    }
    if (!technicalDetails.llmProvider.trim()) {
      setError("LLM Provider is required");
      return false;
    }
    if (!technicalDetails.llmModel.trim()) {
      setError("LLM Model is required");
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!useCaseCatalog.useCaseTitle.trim()) {
      setError("Use Case Title is required");
      return false;
    }
    if (!useCaseCatalog.seriousness) {
      setError("Seriousness is required");
      return false;
    }
    if (!useCaseCatalog.useCaseDescription.trim()) {
      setError("Use Case Description is required");
      return false;
    }
    if (!useCaseCatalog.expectedBehavior.trim()) {
      setError("Expected Behavior is required");
      return false;
    }
    if (!useCaseCatalog.dataSource) {
      setError("Data Source is required");
      return false;
    }
    if (!useCaseCatalog.smeOwner.trim()) {
      setError("SME Owner is required");
      return false;
    }
    return true;
  };

  const handleBasicInfoChange = (e) => {
    const { name, value } = e.target;
    setBasicInfoState((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleTechnicalChange = (e) => {
    const { name, value } = e.target;
    setTechnicalDetailsState((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleUseCaseChange = (e) => {
    const { name, value } = e.target;
    setUseCaseCatalogState((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleAddEndpoint = () => {
    if (technicalDetails.newEndpoint.trim()) {
      setTechnicalDetailsState((prev) => ({
        ...prev,
        apiEndpoints: [...(prev.apiEndpoints || []), prev.newEndpoint],
        newEndpoint: "",
      }));
    }
  };

  const handleRemoveEndpoint = (ep) => {
    setTechnicalDetailsState((prev) => ({
      ...prev,
      apiEndpoints: prev.apiEndpoints.filter((e) => e !== ep),
    }));
  };

  const handleCheck = (tool) => {
    setTechnicalDetailsState((prev) => ({
      ...prev,
      integrationTools: prev.integrationTools.includes(tool)
        ? prev.integrationTools.filter((t) => t !== tool)
        : [...prev.integrationTools, tool],
    }));
  };
  const handleAgentToolToggle = (tool) => {
    setTechnicalDetailsState((prev) => ({
      ...prev,
      agentTools: prev.agentTools.includes(tool)
        ? prev.agentTools.filter((t) => t !== tool)
        : [...prev.agentTools, tool],
    }));
  };

  const handleRiskAssociationCheck = (risk) => {
    setUseCaseCatalogState((prev) => ({
      ...prev,
      riskAssociation: prev.riskAssociation.includes(risk)
        ? prev.riskAssociation.filter((r) => r !== risk)
        : [...prev.riskAssociation, risk],
    }));
  };

  const handleEvaluationScopeCheck = (scope) => {
    setUseCaseCatalogState((prev) => ({
      ...prev,
      evaluationScope: prev.evaluationScope.includes(scope)
        ? prev.evaluationScope.filter((s) => s !== scope)
        : [...prev.evaluationScope, scope],
    }));
  };

  // State for example menu
  const [exampleMenuAnchor, setExampleMenuAnchor] = useState(null);

  // Load example data based on current step and selected example
  // const handleLoadExample = (exampleNumber = 1) => {
  //   const exampleData = exampleNumber === 2 ? EXAMPLE_DATA_2 : EXAMPLE_DATA ;
  const handleLoadExample = (exampleNumber = 1) => {
  const exampleMap = {
    1: EXAMPLE_DATA,
    2: EXAMPLE_DATA_2,
    3: EXAMPLE_DATA_3,
  };

  const exampleData = exampleMap[exampleNumber];

  if (!exampleData) {
    console.warn("Invalid example selected:", exampleNumber);
    return;
  }

    if (activeStep === 0) {
      setBasicInfoState((prev) => ({
        ...prev,
        ...exampleData.step1,
      }));
    } else if (activeStep === 1) {
      setTechnicalDetailsState((prev) => ({
        ...prev,
        ...exampleData.step2,
      }));
    } else if (activeStep === 2) {
      // Add example use case to the list
      setUseCasesListState((prev) => [...prev, ...exampleData.step3]);
    }
    setExampleMenuAnchor(null); // Close menu after selection
  };

  const handleExampleMenuOpen = (event) => {
    setExampleMenuAnchor(event.currentTarget);
  };

  const handleExampleMenuClose = () => {
    setExampleMenuAnchor(null);
  };

  const handleNext = async () => {
    setError(null);

    // Validate current step
    let isValid = false;
    if (activeStep === 0) isValid = validateStep1();
    else if (activeStep === 1) isValid = validateStep2();
    else isValid = true; // Skip validation for Step 2 and Step 3

    if (!isValid) return;

    // Save progress
    setLoading(true);
    dispatch(setAppSetupLoading(true));
    try {
      const response = await axios.post(
        `${baseUrl}/application-setup?project_id=${currentProjectId}`,
        {
          project_id: currentProjectId,
          basicInfo,
          technicalDetails,
          useCaseCatalog,
          useCasesList,
          classificationResult,
          currentStep: activeStep,
        }
      );

      // Update Redux store with saved data
      if (response.data.success && response.data.data) {
        dispatch(setApplicationSetupData(response.data.data));
      }

      setLoading(false);
      dispatch(setAppSetupLoading(false));

      // Move to next step
      if (activeStep < steps.length - 1) {
        setActiveStep((prev) => prev + 1);
      }
    } catch (err) {
      console.error("Error saving application setup:", err);
      setLoading(false);
      dispatch(setAppSetupLoading(false));
      dispatch(setAppSetupError("Failed to save progress"));
      setError("Failed to save progress");
    }
  };

  const handleBack = () => {
    setError(null);
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleGenerateClassification = async () => {
    setGenerating(true);
    setError(null);

    try {
      // This will call the backend endpoint for classification
      console.log("Generating classification...", {
        basicInfo,
        technicalDetails,
        useCaseCatalog,
        useCasesList,
        project_id: currentProjectId,
      });
      const response = await axios.post(
        `${baseUrl}/application-setup/generate-classification`,
        {
          project_id: currentProjectId,
          basicInfo,
          technicalDetails,
          useCaseCatalog,
          useCasesList,
        }
      );

      console.log("Clasification response:", response.data);
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        console.log("Received data: ", data);
        // Backend earlier returned riskProfile inside data — adapt if shape differs
        const rp = response.data.data.riskProfile || response.data.data;
        const riskCode = rp?.code || rp?.riskCode || rp?.archetypeCode || "";
        const riskLevel = rp?.level || rp?.Risk || rp?.riskLevel || "";

        const classificationData = {
          classification: data.classification,
          riskProfile: data.riskProfile,
          recommendations: data.recommendations || [],
        };
        setClassificationResultState(classificationData);
        dispatch(setClassificationResult(classificationData));
      } else {
        setError(response.data.error || "Failed to generate classification");
      }
    } catch (err) {
      console.error("Error generating classification:", err);
      setError("Failed to generate classification. See console for details.");
      setClassificationResultState(null);
    } finally {
      setGenerating(false);
    }
  };
  //     if (response.data.success) {
  //       // Navigate to next page or show success
  //       navigate('/risk-identification');
  //     }
  //     else{
  //       setError(response.data.error || 'Failed to generate classification');
  //     }
  //   } catch (err) {
  //     console.error('Error generating classification: ',err);
  //     setError(err.response?.data?.error || 'Failed to generate classification. Please check if python is installed');
  //   } finally {
  //     setGenerating(false);
  //   }
  // };

  const getStepContent = (step) => {
    switch (step) {
      // case 0:
      //   return (
      //     <Grid container spacing={3}>
      //       <Grid item xs={12}>
      //         <TextField
      //           fullWidth
      //           required
      //           label="Application Name"
      //           name="applicationName"
      //           value={basicInfo.applicationName}
      //           onChange={handleBasicInfoChange}
      //           placeholder="Ex: Insurance Policy Assistant"
      //         />
      //       </Grid>
      //       <Grid item xs={12}>
      //         <TextField
      //           fullWidth
      //           required
      //           label="Domain"
      //           name="domain"
      //           value={basicInfo.domain}
      //           onChange={handleBasicInfoChange}
      //           placeholder="Ex: Insurance"
      //         />
      //       </Grid>
      //       <Grid item xs={12}>
      //         <TextField
      //           fullWidth
      //           label="Description"
      //           name="description"
      //           value={basicInfo.description}
      //           onChange={handleBasicInfoChange}
      //           multiline
      //           rows={4}
      //           placeholder="Enter application description"
      //         />
      //       </Grid>
      //     </Grid>
      //   );
      case 0:
        return (
          <Grid container spacing={3}>
            {/* Application Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Application Name"
                name="applicationName"
                value={basicInfo.applicationName}
                onChange={handleBasicInfoChange}
                placeholder="Ex: Insurance Policy Assistant"
              />
            </Grid>

            {/* Domain */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Domain"
                name="domain"
                value={basicInfo.domain}
                onChange={handleBasicInfoChange}
                placeholder="Ex: Insurance / Healthcare / Finance"
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={basicInfo.description}
                onChange={handleBasicInfoChange}
                multiline
                rows={3}
                placeholder="Provide a brief note about the application purpose"
              />
            </Grid>

            {/* Organization */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Organization"
                name="organization"
                value={basicInfo.organization || ""}
                onChange={handleBasicInfoChange}
                placeholder="Ex: QualiZeal"
              />
            </Grid>

            {/* Deployment Environment */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Deployment Environment</InputLabel>
                <Select
                  name="deploymentEnvironment"
                  value={basicInfo.deploymentEnvironment || ""}
                  onChange={handleBasicInfoChange}
                  label="Deployment Environment"
                >
                  <MenuItem value="Prod">Prod</MenuItem>
                  <MenuItem value="Staging">Staging</MenuItem>
                  <MenuItem value="QA">QA</MenuItem>
                  <MenuItem value="Dev">Dev</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Business Owner */}
            {/* <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Business Owner"
          name="businessOwner"
          value={basicInfo.businessOwner || ''}
          onChange={handleBasicInfoChange}
          placeholder="Ex: Jack Panther, Product Manager"
        />
      </Grid>

      {/* Technical Owner */}
            {/* <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Technical Owner"
          name="technicalOwner"
          value={basicInfo.technicalOwner || ''}
          onChange={handleBasicInfoChange}
          placeholder="Ex: John Panther, Lead Engineer"
        />
      </Grid> */}

            {/* Data Sensitivity */}
            <Grid item xs={12}>
              <Typography fontWeight={600} mb={1}>
                Data Sensitivity*
              </Typography>
              <FormGroup row>
                {[
                  {
                    value: "PII",
                    tooltip: "Personally Identifiable Information",
                  },
                  { value: "PHI", tooltip: "Protected Health Information" },
                  { value: "Financial", tooltip: "Financial Information" },
                  {
                    value: "Confidential",
                    tooltip: "Confidential Business Information",
                  },
                  { value: "None", tooltip: "No Sensitive Data" },
                ].map((opt) => (
                  <Tooltip
                    key={opt.value}
                    title={opt.tooltip}
                    arrow
                    placement="top"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={basicInfo.dataSensitivity.includes(
                            opt.value
                          )}
                          onChange={() =>
                            handleDataSensitivityToggle(opt.value)
                          }
                        />
                      }
                      label={opt.value}
                    />
                  </Tooltip>
                ))}
              </FormGroup>
            </Grid>

            {/* Autonomy Level */}
            <Grid item xs={12}>
              <Typography fontWeight={600} mb={1}>
                Autonomy Level*
              </Typography>
              <FormGroup row>
                {[
                  { value: "Advisory", tooltip: "Advisory" },
                  { value: "Decisioning", tooltip: "Decisioning" },
                  { value: "Action-Taking", tooltip: "Action-Taking" },
                ].map((opt) => (
                  <Tooltip
                    key={opt.value}
                    title={opt.tooltip}
                    arrow
                    placement="top"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={basicInfo.autonomyLevel.includes(opt.value)}
                          onChange={() => handleAutonomyLevelToggle(opt.value)}
                        />
                      }
                      label={opt.value}
                    />
                  </Tooltip>
                ))}
              </FormGroup>
            </Grid>
            {/* Modifiers */}
            <Grid item xs={12}>
              <Typography fontWeight={600} mb={1}>
                Modifiers*
              </Typography>
              <FormGroup row>
                {[
                  { value: "AG", tooltip: "Agentic" },
                  { value: "ENT", tooltip: "Enterprise" },
                  { value: "FT", tooltip: "Fine-Tuned" },
                  { value: "MM", tooltip: "Multi-Model" },
                  { value: "RAG", tooltip: "Retrieval-Augmented Generation" },
                ].map((opt) => (
                  <Tooltip
                    key={opt.value}
                    title={opt.tooltip}
                    arrow
                    placement="top"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={basicInfo.modifiers.includes(opt.value)}
                          onChange={() => handleModifierCheck(opt.value)}
                        />
                      }
                      label={opt.value}
                    />
                  </Tooltip>
                ))}
              </FormGroup>
            </Grid>

            {/* Risk Context Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Risk Context Notes"
                name="riskContextNotes"
                value={basicInfo.riskContextNotes || ""}
                onChange={handleBasicInfoChange}
                multiline
                rows={3}
                placeholder="Enter any contextual notes related to risk, compliance, or data handling"
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Interface Type</InputLabel>
                  <Select
                    name="interfaceType"
                    value={technicalDetails.interfaceType}
                    onChange={handleTechnicalChange}
                    label="Interface Type"
                  >
                    <MenuItem value="Hybrid">Hybrid</MenuItem>
                    <MenuItem value="UI">UI</MenuItem>
                    <MenuItem value="API">API</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  label="LLM Provider"
                  name="llmProvider"
                  value={technicalDetails.llmProvider}
                  onChange={handleTechnicalChange}
                  placeholder="Ex: OpenAI"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  label="LLM Model Name"
                  name="llmModel"
                  value={technicalDetails.llmModel}
                  onChange={handleTechnicalChange}
                  placeholder="Ex: gpt-4o-mini"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Front-End Tech Stack"
                  name="frontendStack"
                  value={technicalDetails.frontendStack}
                  onChange={handleTechnicalChange}
                  placeholder="Ex: React, TypeScript"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Backend Tech Stack"
                  name="backendStack"
                  value={technicalDetails.backendStack}
                  onChange={handleTechnicalChange}
                  placeholder="Ex: Node.js, FastAPI"
                />
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="On-Premises"
                  name="onpremises"
                  value={technicalDetails.backendStack}
                  onChange={handleTechnicalChange}
                  placeholder="Ex: Llama, OpenAI"
                />
              </Grid> */}
              {/* <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cloud"
                  name="cloud"
                  value={technicalDetails.backendStack}
                  onChange={handleTechnicalChange}
                  placeholder="Ex: Azure, Aws, GCP"
                />
              </Grid> */}
            </Grid>

            <Divider sx={{ my: 3 }} />
            <Typography fontWeight={600} mb={1}>
              API Endpoints
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                fullWidth
                placeholder="Enter endpoint URL"
                value={technicalDetails.newEndpoint || ""}
                name="newEndpoint"
                onChange={handleTechnicalChange}
              />
              <Button variant="contained" onClick={handleAddEndpoint}>
                + Add
              </Button>
            </Box>
            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
              {(technicalDetails.apiEndpoints || []).map((ep) => (
                <Chip
                  key={ep}
                  label={ep}
                  onDelete={() => handleRemoveEndpoint(ep)}
                />
              ))}
            </Box>

            <Divider sx={{ my: 3 }} />
            <Typography fontWeight={600} mb={1}>
              RAG Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Embedding Provider"
                  name="ragEmbedding"
                  value={technicalDetails.ragEmbedding}
                  onChange={handleTechnicalChange}
                  placeholder="Ex: OpenAI, Cohere"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Vector Database</InputLabel>
                  <Select
                    label="Vector Database"
                    name="ragVector"
                    value={technicalDetails.ragVector}
                    onChange={handleTechnicalChange}
                  >
                    {VECTOR_DATABASES.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Retrieval Mechanism"
                  name="ragRetrieval"
                  value={technicalDetails.ragRetrieval}
                  onChange={handleTechnicalChange}
                  placeholder="Ex: Similarity Search, Top-K=5"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Authentication Method</InputLabel>
                  <Select
                    label="Authentication Method"
                    name="authMethod"
                    value={technicalDetails.authMethod}
                    onChange={handleTechnicalChange}
                  >
                    <MenuItem value="API Key">API Key</MenuItem>
                    <MenuItem value="OAuth">OAuth</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Authentication Token"
                  name="authToken"
                  value={technicalDetails.authToken}
                  onChange={handleTechnicalChange}
                  placeholder="Enter API Key or Token"
                />
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="AI URL"
                  name="aiUrl"
                  value={technicalDetails.aiUrl}
                  onChange={handleTechnicalChange}
                  placeholder="https://app.example.com"
                />
              </Grid> */}
              {/* <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="UI Input Selector"
                  name="uiInputSelector"
                  value={technicalDetails.uiInputSelector}
                  onChange={handleTechnicalChange}
                  placeholder="#chat-input"
                />
              </Grid> */}
              {/* <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="UI Output Selector"
                  name="uiOutputSelector"
                  value={technicalDetails.uiOutputSelector}
                  onChange={handleTechnicalChange}
                  placeholder="#chat-response"
                />
              </Grid> */}
            </Grid>
            <Divider sx={{ my: 3 }} />
            <Typography fontWeight={600} mb={1}>
              Agent
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="LLM Provider"
                  name="agentEmbedding"
                  value={technicalDetails.agentEmbedding}
                  onChange={handleTechnicalChange}
                  placeholder="Ex: OpenAI, Cohere"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Vector Database</InputLabel>
                  <Select
                    label="Vector Database"
                    name="agentVector"
                    value={technicalDetails.agentVector}
                    onChange={handleTechnicalChange}
                  >
                    {VECTOR_DATABASES.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Retrieval Mechanism"
                  name="agentRetrieval"
                  value={technicalDetails.agentRetrieval}
                  onChange={handleTechnicalChange}
                  placeholder="Ex: Similarity Search"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Authentication Method</InputLabel>
                  <Select
                    label="Authentication Method"
                    name="agentAuthMethod"
                    value={technicalDetails.agentAuthMethod}
                    onChange={handleTechnicalChange}
                  >
                    <MenuItem value="API Key">API Key</MenuItem>
                    <MenuItem value="OAuth">OAuth</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Authentication Token"
                  name="agentAuthToken"
                  value={technicalDetails.agentAuthToken}
                  onChange={handleTechnicalChange}
                  placeholder="Enter API Key or Token"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="AI URL"
                  name="agentUrl"
                  value={technicalDetails.agentUrl}
                  onChange={handleTechnicalChange}
                  placeholder="https://agent.example.com"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="UI Input Selector"
                  name="agentInputSelector"
                  value={technicalDetails.agentInputSelector}
                  onChange={handleTechnicalChange}
                  placeholder="#agent-input"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="UI Output Selector"
                  name="agentOutputSelector"
                  value={technicalDetails.agentOutputSelector}
                  onChange={handleTechnicalChange}
                  placeholder="#agent-output"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />
            <Typography fontWeight={600} mb={1}>
              Integration Tools
            </Typography>
            <FormGroup row>
              {INTEGRATION_TOOLS.map((tool) => (
                <FormControlLabel
                  key={tool}
                  control={
                    <Checkbox
                      checked={technicalDetails.integrationTools.includes(tool)}
                      onChange={() => handleCheck(tool)}
                    />
                  }
                  label={tool}
                />
              ))}
            </FormGroup>
            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label="Deployment Logs API"
              name="logsApi"
              value={technicalDetails.logsApi}
              onChange={handleTechnicalChange}
              placeholder="https://api.example.com/logs"
            />
          </>
        );

      case 2:
        return (
          <Box>
            {/* Three Feature Cards - Matching Figma Design */}
            <Grid container spacing={2.5} mb={4}>
              {/* Upload Use Cases Card */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    p: 3.5,
                    borderRadius: 2,
                    textAlign: "center",
                    height: "100%",
                    border: "1px solid #FFFFFF",
                    // backgroundColor: "#F5F9FF",
                    boxShadow: "none",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,148,251,0.15)",
                      borderColor: "#0094FB",
                      transform: "translateY(-2px)",
                    },
                  }}
                  onClick={() =>
                    document.getElementById("fileInputUpload").click()
                  }
                >
                  {/* Icon and Title in Same Line */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "#F0F9FF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {/* <Upload sx={{ fontSize: 24, color: "#0094FB" }} /> */}
                      <CloudUploadOutlined
                        sx={{ fontSize: 22, color: "#0094FB" }}
                      />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={500}
                      color="#111827"
                    >
                      Upload Use Cases
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={3}
                    sx={{ fontSize: "13px", lineHeight: 0.5 }}
                  >
                    Upload a .doc,.docx,.pdf
                  </Typography>

                  <Button
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      borderRadius: 1.5,
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      fontSize: "14px",
                      backgroundColor: "#0094FB",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#0077CC",
                        boxShadow: "0 2px 8px rgba(0,148,251,0.3)",
                      },
                    }}
                  >
                    Choose File
                  </Button>

                  {/* Hidden File Input */}
                  <input
                    type="file"
                    id="fileInputUpload"
                    accept=".json,.xml"
                    style={{ display: "none" }}
                    onChange={handleChooseFileUpload}
                  />
                </Card>
              </Grid>

              {/* Import Use Cases Card */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    p: 3.5,
                    borderRadius: 2,
                    textAlign: "center",
                    height: "100%",
                    border: "1px solid #FFFFFF",
                    boxShadow: "none",
                    transition: "all 0.2s",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,148,251,0.15)",
                      borderColor: "#0094FB",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {/* Icon and Title in Same Line */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "#F0F9FF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {/* <GetApp sx={{ fontSize: 24, color: "#0094FB" }} /> */}
                      <DownloadOutlined
                        sx={{ fontSize: 22, color: "#0094FB" }}
                      />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={500}
                      color="#111827"
                    >
                      Import Use Cases
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={3}
                    sx={{ fontSize: "13px", lineHeight: 0.5 }}
                  >
                    Import from Jira, ADO, Confluence
                  </Typography>

                  <Button
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      borderRadius: 1.5,
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      fontSize: "14px",
                      backgroundColor: "#0094FB",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#0077CC",
                        boxShadow: "0 2px 8px rgba(0,148,251,0.3)",
                      },
                    }}
                  >
                    Import
                  </Button>
                </Card>
              </Grid>

              {/* Define Use Cases Card */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    p: 3.5,
                    borderRadius: 2,
                    textAlign: "center",
                    height: "100%",
                    border: "1px solid #E5E7EB",
                    boxShadow: "none",
                    transition: "all 0.2s",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,148,251,0.15)",
                      borderColor: "#0094FB",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {/* Icon and Title in Same Line */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "#F0F9FF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {/* <Description sx={{ fontSize: 24, color: "#0094FB" }} /> */}
                      <DescriptionOutlined
                        sx={{ fontSize: 22, color: "#0094FB" }}
                      />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={500}
                      color="#111827"
                    >
                      Define Use Cases
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={3}
                    sx={{ fontSize: "13px", lineHeight: 0.5 }}
                  >
                    Enter use case details manually
                  </Typography>

                  <Button
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      borderRadius: 1.5,
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      fontSize: "14px",
                      backgroundColor: "#0094FB",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#0077CC",
                        boxShadow: "0 2px 8px rgba(0,148,251,0.3)",
                      },
                    }}
                    onClick={() => setShowDefineModal(true)}
                  >
                    Define Use Cases
                  </Button>
                </Card>
              </Grid>
            </Grid>

            {/* Use Cases Section - Matching Figma Design */}
            <Card
              sx={{
                p: 0,
                mt: 3,
                borderRadius: 2,
                backgroundColor: "#fff",
                mb: 3,
                border: "1px solid #E5E7EB",
                boxShadow: "none",
              }}
            >
              {/* Header */}
              <Box sx={{ p: 3, borderBottom: "none" }}>
                <Typography variant="h6" fontWeight={550} color="#111827">
                  Use Cases*
                </Typography>
              </Box>

              {useCasesList.length === 0 ? (
                // Empty State - Matching Figma
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8,
                    px: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      backgroundColor: "#F0F9FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                    }}
                  >
                    <InfoOutlined sx={{ fontSize: 40, color: "#0094FB" }} />
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    mb={1}
                    color="#111827"
                  >
                    No Use Cases defined yet
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", maxWidth: 400 }}
                  >
                    Upload or Import or Define Use Cases to view the Data
                    Entries
                  </Typography>
                </Box>
              ) : (
                // Collapsible Uploaded Use Cases - Matching Figma
                <Box sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      p: 2,
                      mb: 2,
                      borderRadius: 1.5,
                      backgroundColor: "#F9FAFB",
                      transition: "all 0.2s",
                      "&:hover": {
                        backgroundColor: "#F3F4F6",
                      },
                    }}
                    onClick={() => setOpenUseCaseDrawer(!openUseCaseDrawer)}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ flex: 1, color: "#111827" }}
                    >
                      {openUseCaseDrawer ? "▼" : "▶"} Uploaded Use Cases
                    </Typography>
                    <Chip
                      label={`${useCasesList.length}`}
                      size="small"
                      sx={{
                        bgcolor: "#F5F2F2",
                        color: "#0369A1",
                        fontWeight: 700,
                        fontSize: "12px",
                        height: 24,
                        "& .MuiChip-label": { px: 1.5 },
                      }}
                    />
                  </Box>

                  {openUseCaseDrawer && (
                    <Box>
                      {useCasesList.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            border: "1px solid #E5E7EB",
                            borderRadius: 2,
                            p: 2.5,
                            mb: 2,
                            background: "#FAFBFC",
                            transition: "all 0.2s",
                            "&:hover": {
                              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                              borderColor: "#CBD5E1",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1.5,
                            }}
                          >
                            <Chip
                              label={`UC-${String(index + 1).padStart(2, "0")}`}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                bgcolor: "#f3f2f5ff",
                                color: "#1E40AF",
                                fontSize: "11px",
                                height: 22,
                                "& .MuiChip-label": { px: 1.5 },
                              }}
                            />
                          </Box>

                          <Typography
                            fontWeight={700}
                            fontSize="15px"
                            color="#111827"
                            mb={0.5}
                          >
                            {item.title || item.fileName}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "13px", lineHeight: 1.6 }}
                          >
                            {item.description || "No description provided"}
                          </Typography>

                          <Box
                            display="flex"
                            justifyContent="flex-end"
                            gap={1}
                            mt={2}
                          >
                            <Button
                              size="small"
                              sx={{
                                textTransform: "none",
                                color: "#0094FB",
                                fontWeight: 600,
                                fontSize: "13px",
                                "&:hover": {
                                  backgroundColor: "#F0F9FF",
                                },
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              sx={{
                                textTransform: "none",
                                color: "#DC2626",
                                fontWeight: 600,
                                fontSize: "13px",
                                "&:hover": {
                                  backgroundColor: "#FEF2F2",
                                },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setUseCasesListState((prev) =>
                                  prev.filter((_, i) => i !== index)
                                );
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Generate Classification Button - Only visible when use cases exist */}
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleGenerateClassification}
                      disabled={generating || classificationResult !== null}
                      startIcon={
                        generating ? <CircularProgress size={20} /> : null
                      }
                      sx={{
                        textTransform: "none",
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        borderRadius: 1.5,
                        backgroundColor: "#0094FB",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#0077CC",
                          boxShadow: "0 2px 8px rgba(0,148,251,0.3)",
                        },
                      }}
                    >
                      {generating ? "Classifying..." : "Classify Archetype →"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Card>

            {/* Generated Classification Section - Only shown after classification is generated */}
            {classificationResult && (
              <Card
                sx={{
                  p: 4,
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                }}
              >
                <Typography variant="h6" fontWeight={600} mb={3}>
                  Determine AI Archetype
                </Typography>

                <Grid container spacing={3}>
                  {/* Classification Card */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 3,
                        border: "1px solid #E5E7EB",
                        borderRadius: 2,
                        backgroundColor: "#FAFAFA",
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        mb={2}
                        color="#0094FB"
                      >
                        Classification
                      </Typography>
                      <Typography variant="body2" mb={1.5}>
                        <strong>Archetype:</strong>{" "}
                        {classificationResult.classification?.archetype}
                      </Typography>
                      <Typography variant="body2" mb={1.5}>
                        <strong>Description:</strong>{" "}
                        {classificationResult.classification?.description}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Confidence:</strong>{" "}
                        {classificationResult.classification?.confidence
                          ? `${(
                              classificationResult.classification.confidence *
                              100
                            ).toFixed(0)}%`
                          : "N/A"}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Risk Profile Card */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 3,
                        border: "1px solid #E5E7EB",
                        borderRadius: 2,
                        backgroundColor: "#FAFAFA",
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        mb={2}
                        color="#0094FB"
                      >
                        Risk Profile
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1.5,
                          gap: 1,
                        }}
                      >
                        <strong>Risk Level:</strong>
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor:
                              (classificationResult.riskProfile?.level ||
                                classificationResult.riskProfile?.Risk) ===
                              "CRITICAL"
                                ? "#FEE2E2"
                                : (classificationResult.riskProfile?.level ||
                                    classificationResult.riskProfile?.Risk) ===
                                  "HIGH"
                                ? "#FEF3C7"
                                : (classificationResult.riskProfile?.level ||
                                    classificationResult.riskProfile?.Risk) ===
                                  "MEDIUM"
                                ? "#E0F2FE"
                                : "#D1FAE5",
                            color:
                              (classificationResult.riskProfile?.level ||
                                classificationResult.riskProfile?.Risk) ===
                              "CRITICAL"
                                ? "#DC2626"
                                : (classificationResult.riskProfile?.level ||
                                    classificationResult.riskProfile?.Risk) ===
                                  "HIGH"
                                ? "#D97706"
                                : (classificationResult.riskProfile?.level ||
                                    classificationResult.riskProfile?.Risk) ===
                                  "MEDIUM"
                                ? "#0284C7"
                                : "#059669",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                          }}
                        >
                          {classificationResult.riskProfile?.level ||
                            classificationResult.riskProfile?.Risk}
                        </Box>
                      </Box>
                      <Typography variant="body2">
                        <strong>Description:</strong>{" "}
                        {classificationResult.riskProfile?.description}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Focus Areas Card */}
                  {classificationResult.classification?.focusAreas?.length >
                    0 && (
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          p: 3,
                          border: "1px solid #E5E7EB",
                          borderRadius: 2,
                          backgroundColor: "#FAFAFA",
                          height: "100%",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          mb={2}
                          color="#0094FB"
                        >
                          Key Focus Areas
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                          {classificationResult.classification.focusAreas.map(
                            (area, idx) => (
                              <Typography
                                component="li"
                                variant="body2"
                                key={idx}
                                sx={{ mb: 0.75 }}
                              >
                                {area}
                              </Typography>
                            )
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {/* Recommendations Card */}
                  {classificationResult.recommendations?.length > 0 && (
                    <Grid
                      item
                      xs={12}
                      md={
                        classificationResult.classification?.focusAreas
                          ?.length > 0
                          ? 6
                          : 12
                      }
                    >
                      <Box
                        sx={{
                          p: 3,
                          border: "1px solid #E5E7EB",
                          borderRadius: 2,
                          backgroundColor: "#FAFAFA",
                          height: "100%",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          mb={2}
                          color="#0094FB"
                        >
                          TEVV Recommendations
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                          {classificationResult.recommendations.map(
                            (rec, idx) => (
                              <Typography
                                component="li"
                                variant="body2"
                                key={idx}
                                sx={{ mb: 0.75 }}
                              >
                                {rec}
                              </Typography>
                            )
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Card>
            )}
          </Box>
        );

      // case 3:
      //   return (
      //     <Card
      //       sx={{
      //         border: '2px dashed #1976d2',
      //         borderRadius: 2,
      //         p: 4,
      //         textAlign: 'center',
      //         backgroundColor: '#f5f5f5',
      //       }}
      //     >
      //       <Typography variant="h6" fontWeight={600} mb={2}>
      //         Generate System Output
      //       </Typography>
      //       <Typography variant="body2" color="text.secondary" mb={4}>
      //         Complete all sections and click below to generate intelligent archetype classification, risk assessment, and recommendations.
      //       </Typography>
      //       <Button
      //         variant="contained"
      //         size="large"
      //         onClick={handleGenerateClassification}
      //         disabled={generating}
      //         startIcon={generating ? <CircularProgress size={20} /> : null}
      //         sx={{
      //           px: 4,
      //           py: 1.5,
      //         }}
      //       >
      //         {generating ? 'Generating...' : 'Generate Classification'}
      //       </Button>
      //     </Card>
      //   );

      default:
        return "Unknown step";
    }
  };

  const getCurrentStepLabel = () => {
    const stepLabels = [
      "Basic Application Information",
      "Technical Details (UI/API)",
      "Use Case",
    ];
    return stepLabels[activeStep] || "";
  };

  const getNextStepLabel = () => {
    if (activeStep === 0) return "Technical Details (UI/API)";
    if (activeStep === 1) return "Use Case";
    if (activeStep === 2 && classificationResult) return "Trust Dimensions";
    return "";
  };

  return (
    <Box
      sx={{
        backgroundColor: "#F5F2F2",
        minHeight: "100vh",
        ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* === Sticky Header (Matches Trust Index Style) === */}
      <Box
        sx={{
          position: "sticky",
          top: 50,
          zIndex: 120,
          backgroundColor: "#F5F2F2",
          pb: 1.5,
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Box sx={{ py: 2.5, px: 3 }}>
          <Typography variant="h5" fontWeight={600} color="#111827">
            {activeStep === 2 && classificationResult
              ? "Determine AI Archetype"
              : activeStep === 2
              ? "Determine AI Archetype"
              : "Determine AI Archetype"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeStep === 2 && classificationResult
              ? "AI System Characterization & Capability Profiling..."
              : activeStep === 2
              ? "AI System Characterization & Capability Profiling..."
              : "AI System Characterization & Capability Profiling..."}
          </Typography>
        </Box>

        {/* NOW & NEXT Row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: 3,
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography fontWeight={300}>
              Now: <strong>{getCurrentStepLabel()}</strong>
            </Typography>
            <Tooltip title="Load Example Data" arrow>
              <IconButton
                size="small"
                onClick={handleExampleMenuOpen}
                sx={{
                  color: "#0094fb",
                  "&:hover": {
                    backgroundColor: "#e3f2fd",
                  },
                }}
              >
                <CloudDownload fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={exampleMenuAnchor}
              open={Boolean(exampleMenuAnchor)}
              onClose={handleExampleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <MenuItem onClick={() => handleLoadExample(1)}>
                <ListItemIcon>
                  <HealthAndSafety fontSize="small" sx={{ color: "#4caf50" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Example 1: Insurance RAG"
                  secondary="Life Guard Health Insurance - RAG-based chatbot"
                />
              </MenuItem>
              <MenuItem onClick={() => handleLoadExample(2)}>
                <ListItemIcon>
                  <Visibility fontSize="small" sx={{ color: "#2196f3" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Example 2: Multi-media"
                  secondary="Image validation"
                />
              </MenuItem>
              <MenuItem onClick={() => handleLoadExample(3)}>
                <ListItemIcon>
                  <Visibility fontSize="small" sx={{ color: "#2196f3" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Example 3: HealthCare"
                  secondary="RAG"
                />
              </MenuItem>
                          </Menu>
          </Box>

          {getNextStepLabel() && (
            <Typography fontWeight={300}>
              Next: <strong>{getNextStepLabel()}</strong>
            </Typography>
          )}
        </Box>

        {/* Step Progress Bar */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, px: 3, pb: 1 }}
        >
          <Box sx={{ flex: 1, display: "flex", gap: 0.5 }}>
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  height: 6,
                  borderRadius: 1,
                  backgroundColor: index <= activeStep ? "#1976d2" : "#D1D5DB",
                }}
              />
            ))}
          </Box>

          <Typography variant="body2" fontWeight={600} color="#1976d2">
            Step {activeStep + 1}/3
          </Typography>
        </Box>
      </Box>

      {/* === Main Form Card === */}
      <Box
        sx={{
          backgroundColor: "#F3F4F6",
          borerRadius: 2,
          p: 0.5,
          mt: 3,
          mx: 3,
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {getStepContent(activeStep)}

        {/* ---------------------- BEGIN DRAWERS ---------------------- */}

        {/* Upload Use Cases Drawer */}
        <Drawer
          anchor="right"
          open={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          PaperProps={{
            sx: { width: 420, p: 3, borderRadius: "12px 0 0 12px" },
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={2}>
            Upload Use Cases
          </Typography>

          <Box
            onDrop={handleFileDrop}
            onDragOver={(e) => e.preventDefault()}
            sx={dropAreaStyle}
          >
            {uploadedFile ? (
              <Typography fontWeight={600}>{uploadedFile.name}</Typography>
            ) : (
              <Typography>Drag & Drop or Click to Browse</Typography>
            )}

            <input
              id="fileUpload"
              type="file"
              style={{ display: "none" }}
              onChange={handleBrowseFile}
            />
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => document.getElementById("fileUpload").click()}
            >
              Browse Files
            </Button>
          </Box>

          <Box display="flex" justifyContent="flex-end" mt={3} gap={1}>
            <Button onClick={() => setShowUploadModal(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!uploadedFile}
              onClick={handleUploadFile}
            >
              Upload
            </Button>
          </Box>
        </Drawer>

        {/* Define Use Case Drawer */}
        <Drawer
          anchor="right"
          open={showDefineModal}
          onClose={() => setShowDefineModal(false)}
          PaperProps={{
            sx: { width: 450, p: 3, borderRadius: "12px 0 0 12px" },
          }}
        >
          <Typography variant="h6" fontWeight={700} mb={2}>
            Define Use Case
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Use Case Title"
                name="title"
                value={defineCaseForm.title}
                onChange={handleDefineInput}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Severity</InputLabel>
                <Select
                  name="severity"
                  value={defineCaseForm.severity}
                  onChange={handleDefineInput}
                >
                  {SERIOUSNESS_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                name="description"
                value={defineCaseForm.description}
                onChange={handleDefineInput}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Expected Behavior"
                name="expected"
                value={defineCaseForm.expected}
                onChange={handleDefineInput}
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="space-between" mt={3}>
            <Button onClick={() => setShowDefineModal(false)}>← Back</Button>
            <Button variant="contained" onClick={handleSaveDefinedUseCase}>
              Save →
            </Button>
          </Box>
        </Drawer>

        {/* ---------------------- END DRAWERS ---------------------- */}

        {/* Buttons Section */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0 || loading || generating}
          >
            Back
          </Button>

          {/* Show Next button for steps 0 and 1, show Trust Dimensions button only on step 2 with classification */}
          {activeStep < 2 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading || generating}
            >
              {loading ? "Saving..." : "Next →"}
            </Button>
          ) : activeStep === 2 && classificationResult ? (
            <Button
              variant="contained"
              onClick={async () => {
                // Save all data before navigating to next module
                setLoading(true);
                try {
                  await axios.post(
                    `${baseUrl}/application-setup`,
                    {
                      project_id: currentProjectId,
                      basicInfo,
                      technicalDetails,
                      useCaseCatalog,
                      useCasesList,
                      classificationResult,
                      currentStep: 2,
                    }
                  );
                  console.log("Application setup data saved before navigation");
                } catch (err) {
                  console.error("Error saving application setup:", err);
                  // Continue with navigation even if save fails
                }
                setLoading(false);

                navigate(`/project/${projectId}/trust-index-settings`, {
                  state: {
                    applicationName: basicInfo.applicationName,
                    domain: basicInfo.domain,
                    archetype: classificationResult.classification?.archetype,
                    archetypeDescription:
                      classificationResult.classification?.description,
                    confidence: classificationResult.classification?.confidence,
                    focusAreas:
                      classificationResult.classification?.focusAreas || [],
                    riskCode: classificationResult.riskProfile?.code,
                    riskLevel:
                      classificationResult.riskProfile?.Risk ||
                      classificationResult.riskProfile?.level,
                    description: classificationResult.riskProfile?.description,
                    modifiers: basicInfo.modifiers,
                    useCases: useCasesList,
                    recommendations: classificationResult.recommendations || [],
                  },
                });
              }}
              disabled={generating || loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {loading ? "Saving..." : "Go to Trust Attributes →"}
            </Button>
          ) : null}
        </Box>

        {/* Loading Overlay */}
        <LoadingOverlay open={generating} message="Classifying Archetype..." />
      </Box>
    </Box>
  );
};

export default ApplicationSetupPage;
